/**
 * Base CSV Writer
 *
 * Orchestrates the CSV export process:
 * 1. Get the appropriate platform writer (with version)
 * 2. Validate campaign data
 * 3. Transform to platform format
 * 4. Generate CSV/XLSX file
 * 5. Return downloadable blob
 *
 * Usage:
 *   const result = await exportCampaign(campaignData, { platform: 'meta' });
 *   if (result.success) downloadBlob(result.blob, result.filename);
 */

import {
  CampaignData,
  ExportOptions,
  ExportResult,
  Platform,
} from './types';
import { getWriter } from './platform-writers';
import {
  generateCSV,
  generateXLSX,
  generateFilename,
} from './utils/csv-generator';

/**
 * Export a single campaign to a platform-specific CSV/XLSX
 *
 * @param data - Campaign data in canonical format
 * @param options - Export options (platform, format, version)
 * @returns ExportResult with success status, blob, and any warnings/errors
 */
export async function exportCampaign(
  data: CampaignData,
  options: ExportOptions
): Promise<ExportResult> {
  const { platform, format = 'csv', version } = options;

  try {
    // Get platform-specific writer (latest version by default)
    const writer = getWriter(platform, version);

    // Validate campaign data for this platform
    const validation = writer.validate(data);

    if (!validation.valid) {
      return {
        success: false,
        filename: '',
        blob: new Blob(),
        warnings: validation.warnings,
        errors: validation.errors,
      };
    }

    // Transform canonical data to platform-specific rows
    const rows = writer.transform(data);
    const headers = writer.getHeaders();

    // Generate file
    const filename = generateFilename(data.title, platform, format);
    const blob =
      format === 'xlsx'
        ? await generateXLSX(headers, rows, writer.formatName)
        : generateCSV(headers, rows);

    return {
      success: true,
      filename,
      blob,
      warnings: validation.warnings,
      errors: [],
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    return {
      success: false,
      filename: '',
      blob: new Blob(),
      warnings: [],
      errors: [`Export failed: ${message}`],
    };
  }
}

/**
 * Batch export to multiple platforms
 * Returns a map of platform -> ExportResult
 *
 * @param data - Campaign data in canonical format
 * @param platforms - Array of platforms to export to
 * @param format - File format ('csv' or 'xlsx')
 * @returns Map of platform to ExportResult
 */
export async function exportCampaignBatch(
  data: CampaignData,
  platforms: Platform[],
  format: 'csv' | 'xlsx' = 'csv'
): Promise<Map<Platform, ExportResult>> {
  const results = new Map<Platform, ExportResult>();

  // Export to each platform in parallel
  const exports = await Promise.all(
    platforms.map(async (platform) => {
      const result = await exportCampaign(data, { platform, format });
      return { platform, result };
    })
  );

  exports.forEach(({ platform, result }) => {
    results.set(platform, result);
  });

  return results;
}

/**
 * Download all exports as a ZIP file (for batch export)
 *
 * @param results - Map of platform to ExportResult from batch export
 * @param campaignTitle - Campaign title for the ZIP filename
 * @returns Promise resolving to ZIP blob
 */
export async function downloadAsZip(
  results: Map<Platform, ExportResult>,
  campaignTitle: string
): Promise<Blob> {
  // Dynamic import for code splitting
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();

  // Add successful exports to ZIP
  results.forEach((result, platform) => {
    if (result.success && result.blob.size > 0) {
      zip.file(result.filename, result.blob);
    }
  });

  // Generate ZIP
  return zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });
}

/**
 * Validate campaign data for a specific platform without generating export
 * Useful for showing validation errors before user clicks export
 *
 * @param data - Campaign data
 * @param platform - Target platform
 * @param version - Optional version
 * @returns ValidationResult with errors and warnings
 */
export function validateForPlatform(
  data: CampaignData,
  platform: Platform,
  version?: string
) {
  try {
    const writer = getWriter(platform, version);
    return writer.validate(data);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    return {
      valid: false,
      errors: [message],
      warnings: [],
    };
  }
}
